import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    clientSecret: String,
    successUrl: String,
    publishableKey: String,
    shippingUrl: String,
    cartId: Number,
    email: String,
    deliveryCountry: String
  }

  static targets = ["linkAuth", "addressElement", "paymentElement", "submitButton", "errors", "overlay", "stickyCta"]

  connect() {
    this.initializeElements()
    this.hideStickyCtaOnKeyboard()
  }

  disconnect() {
    if (this.viewportHandler && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.viewportHandler)
    }
  }

  // iOS keeps fixed elements above the virtual keyboard — hide the sticky CTA when it opens
  hideStickyCtaOnKeyboard() {
    if (!window.visualViewport || !this.hasStickyCtaTarget) return

    const threshold = 150
    this.viewportHandler = () => {
      const keyboardOpen = window.innerHeight - window.visualViewport.height > threshold
      this.stickyCtaTarget.style.display = keyboardOpen ? "none" : ""
    }
    window.visualViewport.addEventListener("resize", this.viewportHandler)
  }

  initializeElements() {
    this.stripe = Stripe(this.publishableKeyValue)
    this.elements = this.stripe.elements({
      clientSecret: this.clientSecretValue,
      appearance: {
        theme: "flat",
        variables: {
          fontFamily: "'DM Sans', sans-serif",
          fontSizeBase: "16px",
          fontSizeSm: "12px",
          colorPrimary: "#C88B7E",
          colorText: "#1C1A18",
          colorTextSecondary: "#6B5E52",
          colorTextPlaceholder: "#B8A99A",
          colorBackground: "#FFFFFF",
          borderRadius: "8px",
          spacingUnit: "4px",
          spacingGridRow: "14px",
          spacingGridColumn: "14px"
        },
        rules: {
          ".Input": {
            border: "1.5px solid #E8DDD6",
            padding: "11px 14px",
            fontSize: "16px",
            transition: "border-color 0.2s"
          },
          ".Input:focus": {
            border: "1.5px solid #1C1A18",
            boxShadow: "none"
          },
          ".Label": {
            fontSize: "12px",
            fontWeight: "500",
            color: "#6B5E52"
          }
        }
      },
      locale: "fr"
    })

    const linkAuthElement = this.elements.create("linkAuthentication", {
      defaultValues: { email: this.emailValue || "" }
    })
    linkAuthElement.mount(this.linkAuthTarget)

    const country = this.deliveryCountryValue || "FR"
    const addressElement = this.elements.create("address", {
      mode: "shipping",
      fields: { phone: "always" },
      validation: { phone: { required: "always" } },
      display: { name: "split" },
      defaultValues: { address: { country } },
      allowedCountries: [country]
    })
    addressElement.mount(this.addressElementTarget)

    const paymentElement = this.elements.create("payment", {
      terms: { card: "never" },
      fields: { billingDetails: { address: "if_required" } }
    })
    paymentElement.mount(this.paymentElementTarget)
  }

  toggleSubmit() {
    if (this.hasCgvTarget && this.hasSubmitButtonTarget) {
      this.setSubmitButtonsDisabled(!this.cgvTarget.checked)
    }
  }

  async submit(event) {
    event.preventDefault()
    this.errorsTarget.textContent = ""
    this.setSubmitButtonsDisabled(true)

    const { error: submitError } = await this.elements.submit()
    if (submitError) {
      this.showError(submitError.message)
      return
    }

    // Save shipping address to cart before confirming with Stripe
    const addressElement = this.elements.getElement("address")
    const { complete, value: addressValue } = await addressElement.getValue()
    if (complete) {
      await this.saveShippingAddress(addressValue)
    }

    this.showOverlay()
    const result = await this.stripe.confirmPayment({
      elements: this.elements,
      confirmParams: { return_url: this.successUrlValue }
    })

    if (result.error) {
      this.hideOverlay()
      this.showError(result.error.message)
    }
  }

  async saveShippingAddress(addressValue) {
    const meta = document.querySelector("meta[name='csrf-token']")
    const csrfToken = meta && meta.content

    await fetch(this.shippingUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        name: addressValue.name,
        address: addressValue.address,
        mobile_phone: addressValue.phone
      })
    })
  }

  showOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("visible")
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("visible")
  }

  showError(message) {
    this.errorsTarget.textContent = message
    this.setSubmitButtonsDisabled(false)
  }

  setSubmitButtonsDisabled(disabled) {
    this.submitButtonTargets.forEach((button) => {
      button.disabled = disabled
    })
  }
}
